import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  completeRegistration,
  requestRegistrationOtp,
  verifyRegistrationOtp,
} from "../api/endpoints/auth"
import { setPortalLanguage } from "../i18n"
import { useAuth } from "../stores/auth"

type RegisterLocationState = {
  phoneNumber?: string
  registrationToken?: string
  preferredLanguage?: "en" | "sn"
} | null

export function RegisterPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { setSession } = useAuth()
  const handoff = location.state as RegisterLocationState
  const hasVerifiedHandoff = Boolean(handoff?.phoneNumber && handoff?.registrationToken)
  const [step, setStep] = useState<"phone" | "otp" | "details">(
    hasVerifiedHandoff ? "details" : "phone",
  )
  const [phoneNumber, setPhoneNumber] = useState(handoff?.phoneNumber ?? "+263")
  const [otp, setOtp] = useState("")
  const [registrationToken, setRegistrationToken] = useState(handoff?.registrationToken ?? "")
  const [fullName, setFullName] = useState("")
  const [dateOfBirth, setDateOfBirth] = useState("")
  const [gender, setGender] = useState<"MALE" | "FEMALE" | "OTHER">("MALE")
  const [altPhone, setAltPhone] = useState("")
  const [physicalAddress, setPhysicalAddress] = useState("")
  const [nationalId, setNationalId] = useState("")
  const [preferredLanguage, setPreferredLanguage] = useState<"en" | "sn">(
    handoff?.preferredLanguage ??
      ((typeof window !== "undefined"
        ? window.localStorage.getItem("portal_language")
        : null) as "en" | "sn" | null) ??
      "en",
  )
  const [nextOfKinName, setNextOfKinName] = useState("")
  const [nextOfKinRelationship, setNextOfKinRelationship] = useState("")
  const [nextOfKinPhone, setNextOfKinPhone] = useState("+263")
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setPortalLanguage(preferredLanguage)
  }, [preferredLanguage])

  async function handleRequestOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      const response = await requestRegistrationOtp(phoneNumber)
      setFeedback(response.data.message)
      setStep("otp")
    } catch (requestError) {
      setError((requestError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleVerifyOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      const response = await verifyRegistrationOtp(phoneNumber, otp)
      setRegistrationToken(response.data.registrationToken)
      setStep("details")
    } catch (verifyError) {
      setError((verifyError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleCompleteRegistration(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      const response = await completeRegistration({
        registrationToken,
        fullName,
        dateOfBirth,
        gender,
        altPhone: altPhone || null,
        physicalAddress,
        nationalId,
        preferredLanguage,
        nextOfKin: {
          fullName: nextOfKinName,
          relationship: nextOfKinRelationship,
          phoneNumber: nextOfKinPhone,
        },
      })

      setSession(response.data.client, response.data.expiresAt)
      navigate("/onboarding")
    } catch (completeError) {
      setError((completeError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="auth-shell">
      <section className="auth-card">
        <p className="eyebrow">New borrower onboarding</p>
        <h1>Register your borrower account</h1>
        <p className="auth-copy">
          Verify your phone number, complete your profile, then upload KYC documents before you
          apply.
        </p>

        <label>
          Language
          <select
            onChange={(event) => setPreferredLanguage(event.target.value as "en" | "sn")}
            value={preferredLanguage}
          >
            <option value="en">English</option>
            <option value="sn">chiShona</option>
          </select>
        </label>

        {feedback ? <div className="status-banner success">{feedback}</div> : null}
        {error ? <div className="status-banner error">{error}</div> : null}

        {step === "phone" ? (
          <form className="auth-form" onSubmit={handleRequestOtp}>
            <label>
              Phone number
              <input
                autoComplete="tel"
                inputMode="tel"
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="+263771234567"
                required
                type="tel"
                value={phoneNumber}
              />
            </label>
            <button className="primary-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Sending OTP..." : "Verify phone number"}
            </button>
            <button
              className="ghost-button"
              onClick={() => navigate("/login")}
              type="button"
            >
              Back to sign in
            </button>
          </form>
        ) : null}

        {step === "otp" ? (
          <form className="auth-form" onSubmit={handleVerifyOtp}>
            <label>
              One-time password
              <input
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setOtp(event.target.value)}
                placeholder="123456"
                required
                value={otp}
              />
            </label>
            <button className="primary-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Verifying..." : "Continue"}
            </button>
            <button className="ghost-button" onClick={() => setStep("phone")} type="button">
              Change phone number
            </button>
          </form>
        ) : null}

        {step === "details" ? (
          <form className="form-grid" onSubmit={handleCompleteRegistration}>
            <p className="subtle">Verified phone number: {phoneNumber}</p>
            <label>
              Full name
              <input onChange={(event) => setFullName(event.target.value)} required value={fullName} />
            </label>
            <label>
              Date of birth
              <input
                onChange={(event) => setDateOfBirth(event.target.value)}
                required
                type="date"
                value={dateOfBirth}
              />
            </label>
            <label>
              Gender
              <select onChange={(event) => setGender(event.target.value as any)} value={gender}>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </label>
            <label>
              Alternative phone
              <input onChange={(event) => setAltPhone(event.target.value)} type="tel" value={altPhone} />
            </label>
            <label>
              Physical address
              <textarea
                onChange={(event) => setPhysicalAddress(event.target.value)}
                required
                rows={3}
                value={physicalAddress}
              />
            </label>
            <label>
              National ID
              <input
                onChange={(event) => setNationalId(event.target.value)}
                required
                value={nationalId}
              />
            </label>
            <label>
              Next of kin name
              <input
                onChange={(event) => setNextOfKinName(event.target.value)}
                required
                value={nextOfKinName}
              />
            </label>
            <label>
              Relationship
              <input
                onChange={(event) => setNextOfKinRelationship(event.target.value)}
                required
                value={nextOfKinRelationship}
              />
            </label>
            <label>
              Next of kin phone
              <input
                onChange={(event) => setNextOfKinPhone(event.target.value)}
                required
                type="tel"
                value={nextOfKinPhone}
              />
            </label>
            <button className="primary-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Creating account..." : "Create borrower account"}
            </button>
          </form>
        ) : null}
      </section>
    </div>
  )
}
